import { cloneDeep } from 'lodash';

import {
    CHANGE_COLUMNS_LOG_ORDER,
    CHANGE_COLUMNS_LOG_VISIBILITY,
    CHANGE_MAX_HEIGHT_CLEARED,
    EXPAND_ALL_ROWS,
    EXPAND_ROW,
    LogAction,
    SET_CLIENT_DATA,
    SET_LOG_DATA,
    SET_STUB_DATA,
    UPDATE_COLUMNS_SETTINGS_LOG_STATE,
    UPDATE_LOG_COLUMNS_WIDTH,
    UPDATE_LOG_DATA,
    UPDATE_LOG_SOURCE_PARAMS
} from '../actions/log';
import { LogHelpers, LogInitialState } from '../service/LogHelpers/LogHelpers';
import { maxHeightHelper } from '../service/MaxHeightHelper';
import { logs } from '../typings/common';
import { GridRow } from '../typings/domain';
import { GridExpansion } from '../typings/grid';
import { defaultLogRowsCount, LogEntry } from '../typings/log';
import { LogState, State } from '../typings/state';

function countNewTotal(
    currentLength: number,
    nextPageToken: string | null
): number {
    return currentLength + (nextPageToken === null ? 0 : defaultLogRowsCount);
}

function mergeMaps<T>(
    map1: Map<number, T>,
    map2: Map<number, T>
): Map<number, T> {
    map2.forEach((value: T, key: number) => {
        map1.set(key, value);
    });
    return map1;
}

function updateRowsExpanded(
    current: Map<number, boolean>,
    allRows: Array<GridRow>,
    allExpanded: boolean
): Map<number, boolean> {
    if (!allExpanded) {
        return current;
    }
    return setAllRowsExpanded(allRows, allExpanded);
}

function setAllRowsExpanded(
    allRows: Array<GridRow>,
    isExpanded: boolean
): Map<number, boolean> {
    const expands = new Map<number, boolean>();

    allRows.forEach((row: LogEntry) => {
        expands.set(row.index, isExpanded);
    });
    return expands;
}

export function log(
    state: LogState = LogInitialState,
    action: LogAction,
    fullState: State
): LogState {
    let rowIndex: number,
        currentExpands: Map<number, boolean>,
        currentCanExpand: Map<number, boolean>,
        gridExpansion: GridExpansion,
        stateRowExpanded: Map<number, boolean>,
        data: Array<GridRow> | undefined,
        nextPageToken: string | null,
        total: number;

    switch (action.type) {
        case SET_CLIENT_DATA:
            return {
                ...state,
                login: action.login,
                isClient: action.isClient
            };
        case SET_LOG_DATA:
            data = action.gridData.data;
            nextPageToken = action.nextPageToken;
            total = countNewTotal(data.length, nextPageToken);

            if (
                data.length === 0 &&
                state.gridData.data.length === 0 &&
                !state.gridData.total
            ) {
                return state;
            }

            return {
                ...state,
                gridData: {
                    data: data,
                    total: total,
                    allItemsIds: []
                },
                rowExpanded: new Map<number, boolean>(),
                rowsCanExpand: new Map<number, boolean>(action.rowsCanExpand),
                nextPageToken: action.nextPageToken
            };
        case UPDATE_LOG_DATA:
            data = cloneDeep(state.gridData.data).concat(action.gridData.data);
            currentCanExpand = mergeMaps(
                new Map<number, boolean>(state.rowsCanExpand),
                action.rowsCanExpand
            );
            stateRowExpanded = updateRowsExpanded(
                new Map<number, boolean>(state.rowExpanded),
                data,
                state.allExpanded
            );
            nextPageToken = action.nextPageToken;
            total = countNewTotal(data.length, nextPageToken);

            return {
                ...state,
                gridData: {
                    data: data,
                    total: total,
                    allItemsIds: []
                },
                rowExpanded: stateRowExpanded,
                rowsCanExpand: currentCanExpand,
                nextPageToken: nextPageToken
            };
        case SET_STUB_DATA:
            return {
                ...state,
                gridData: {
                    data: [],
                    allItemsIds: [],
                    total: defaultLogRowsCount
                },
                rowExpanded: new Map<number, boolean>(),
                rowsCanExpand: new Map<number, boolean>(),
                nextPageToken: null
            };
        case UPDATE_LOG_SOURCE_PARAMS:
            return {
                ...state,
                sourceParams: cloneDeep(action.params),
                allExpanded: false
            };
        case UPDATE_LOG_COLUMNS_WIDTH:
            currentCanExpand = new Map<number, boolean>(state.rowsCanExpand);
            currentExpands = new Map<number, boolean>(state.rowExpanded);
            const gridData = cloneDeep(state.gridData.data) as Array<LogEntry>;
            let updatedData: {
                data: Array<LogEntry>;
                rowsCanExpand: Map<number, boolean>;
            };

            // если изменилась ширина колонки с данными логов (где может быть расхлоп)
            // нужно пересчитывать заново необходимость расхлопа и максимальную высоту ряда
            if (
                Boolean(action.changedValueWidth) &&
                state.changedValueWidth !== action.changedValueWidth
            ) {
                // считаем максимальную длину строки в символах
                const maxDiffLength = LogHelpers.getMaxSymbolsByColumnWidth(
                    action.changedValueWidth
                );
                // перерасчитываем необходимость экспанда для всей таблицы
                updatedData = recountNeedExpand(
                    currentCanExpand,
                    gridData,
                    maxDiffLength
                );
                // если какие-то ряды были развернуты, но у них пропала необходимость экспанда
                // сворачиваем обратно
                currentExpands.forEach((isExpanded: boolean, index: number) => {
                    if (!updatedData.rowsCanExpand.get(index) && isExpanded) {
                        currentExpands.set(index, false);
                    }
                });

                // сбрасываем максимальные высоты
                // чтобы при последующей перерисовке они пересчитались
                maxHeightHelper.clear();

                return {
                    ...state,
                    gridData: {
                        data: updatedData ? updatedData.data : gridData,
                        total: state.gridData.total,
                        allItemsIds: state.gridData.allItemsIds
                    },
                    rowExpanded: currentExpands,
                    rowsCanExpand: updatedData
                        ? updatedData.rowsCanExpand
                        : currentCanExpand,
                    columnsSettings: cloneDeep(action.columns),
                    changedValueWidth: action.changedValueWidth
                        ? action.changedValueWidth
                        : state.changedValueWidth,
                    maxHeightCleared: true
                };
            }

            // если изменялась ширина другой колонки - просто кладем в стор
            return {
                ...state,
                columnsSettings: cloneDeep(action.columns)
            };
        case CHANGE_MAX_HEIGHT_CLEARED:
            // опускаем флаг необходимости перерисовки
            return {
                ...state,
                maxHeightCleared: false
            };
        case CHANGE_COLUMNS_LOG_VISIBILITY:
            return {
                ...state,
                columnsSettings: cloneDeep(action.columns)
            };
        case CHANGE_COLUMNS_LOG_ORDER:
            return {
                ...state,
                columnsSettings: {
                    ...state.columnsSettings,
                    [logs]: {
                        ...state.columnsSettings[logs],
                        columnOrder: action.columnOrder
                    }
                }
            };
        case EXPAND_ROW:
            gridExpansion = action.gridExpansion;
            rowIndex = gridExpansion.rowIndex;
            currentExpands = new Map<number, boolean>(state.rowExpanded);
            currentExpands.set(rowIndex, gridExpansion.isExpanded);

            return {
                ...state,
                rowExpanded: currentExpands
            };
        case EXPAND_ALL_ROWS:
            const expands = setAllRowsExpanded(
                state.gridData.data,
                action.isExpanded
            );

            return {
                ...state,
                rowExpanded: expands,
                allExpanded: action.isExpanded
            };

        case UPDATE_COLUMNS_SETTINGS_LOG_STATE:
            return {
                ...state,
                columnsSettings: cloneDeep(action.columnsSettings)
            };

        default:
            return state;
    }
}

export function recountNeedExpand(
    source: Map<number, boolean>,
    gridData: Array<LogEntry>,
    diffLength: number
): { data: Array<LogEntry>; rowsCanExpand: Map<number, boolean> } {
    return gridData.reduce(
        (
            res: { data: Array<LogEntry>; rowsCanExpand: Map<number, boolean> },
            entry: LogEntry
        ) => {
            const {
                    changedValue: { maxLineLength, alwaysNeedExpand },
                    bannersNo,
                    groupsNo
                } = entry,
                needExpand = LogHelpers.calcNeedExpand(
                    alwaysNeedExpand,
                    maxLineLength,
                    diffLength
                );
            // список рядов, которые могут расхлопываться
            // умеют расхлопываться ячейки "что изменилось", "список баннеров", "список групп"
            res.rowsCanExpand.set(
                entry.index,
                needExpand || bannersNo.length > 1 || groupsNo.length > 1
            );
            // необходимость расхлопа конкретной ячейки "что изменилось"
            res.data[entry.index].changedValue.needExpand = needExpand;
            return res;
        },
        { data: gridData, rowsCanExpand: source }
    );
}
